import * as React from 'react';

import classnames from 'classnames';

import { TokenColor } from 'cosmos-tokens-badoo-mobile/badoo/core-token-types';
import Icon from 'components/icon/icon';
import type { AssetProps } from 'components/badoo/asset/asset';
import Loader from 'components/loader/loader';
import Link from 'next/link';

export interface ButtonProps {
    variant?: 'filled' | 'stroke' | 'transparent' | 'custom';
    color?:
        | TokenColor.Brand.Primary
        | TokenColor.Mono.White
        | TokenColor.Providers
        | TokenColor.Generic;
    size?: 'medium' | 'small';
    narrow?: boolean;
    icon?: AssetProps['name'];
    iconPosition?: 'start' | 'end';
    text?: string;
    tag?: 'button' | 'a';
    isDisabled?: boolean;
    isPressed?: boolean;
    isLoading?: boolean;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    linkAttrs?: {
        href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
        rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
        target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
    };
    buttonAttrs?: {
        type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
    };
    innerRef?: any;
    dataQA?: {
        [dataQaAttribute: string]: string;
    };
    a11y?: {
        label?: string;
        loaderLabel?: string;
    };
    extraClass?: any;
    extraAttrs?: any;
}

const Button: React.FC<ButtonProps> = ({
    variant = 'filled',
    color = 'primary',
    size = 'small',
    narrow,
    icon,
    iconPosition = 'start',
    text,
    tag = 'button',
    isDisabled,
    isPressed,
    isLoading,
    onClick,
    linkAttrs,
    buttonAttrs,
    innerRef,
    dataQA,
    a11y,
    extraClass,
    extraAttrs,
}) => {
    const className = classnames({
        'csms-button': true,
        [`csms-button--${variant}`]: true,
        [`csms-button--color-${color}`]: color,
        [`csms-button--${size}`]: true,
        'csms-button--narrow': narrow,
        'is-pressed': (isPressed || isLoading) && !isDisabled,
        'is-loading': isLoading,
        [extraClass!]: extraClass,
    });

    const Tag = tag;

    const commonProps = {
        className,
        onClick: !isDisabled ? onClick : undefined,
        disabled: isDisabled,
        'aria-busy': isLoading,
        ref: innerRef,
        'data-qa': 'button',
        ...dataQA,
        ...extraAttrs,
    };

    const hasOverrideLabel = Boolean(a11y?.label);

    const renderIcon = (position: string) => (
        <span className={`csms-button__icon csms-button__icon--${position}`} aria-hidden={true}>
            {icon && <Icon size="stretch" name={icon} />}
        </span>
    );

    const buttonContent = (
        <React.Fragment>
            <span className="csms-button__content">
                {icon && iconPosition === 'start' && renderIcon('start')}
                {text ? (
                    <span className="csms-button__text" aria-hidden={isLoading || hasOverrideLabel}>
                        <span>{text}</span>
                    </span>
                ) : null}
                {icon && iconPosition === 'end' && renderIcon('end')}
            </span>

            {isLoading ? (
                <span className="csms-button__loader">
                    <Loader />
                </span>
            ) : null}
        </React.Fragment>
    );

    if (tag === 'a') {
        return (
            // @ts-ignore
            <Link href={linkAttrs?.href} legacyBehavior={true}>
                <Tag
                    {...commonProps}
                    role={isDisabled || !linkAttrs?.href ? 'link' : undefined}
                    tabIndex={!linkAttrs?.href && onClick && !isDisabled ? 0 : undefined}
                    {...linkAttrs}
                    href={!isDisabled ? linkAttrs?.href : undefined}
                >
                    {buttonContent}
                </Tag>
            </Link>
        );
    } else {
        const combinedButtonAttrs = { type: 'button', ...buttonAttrs };

        return (
            <Tag {...commonProps} {...combinedButtonAttrs}>
                {buttonContent}
            </Tag>
        );
    }
};

export default Button;
